/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Building2, PlusCircle } from 'lucide-react';
import { aiAgentService } from '../services/aiAgentService';
import {
  knowledgeBaseService, AI_AGENT_ERROR_CODES, getApiErrorMessage
} from '../services/knowledgeBaseService';
import { accountService } from '../services/accountService';
import AiAgentFormSections, {
  parseMcpServers, validateAiAgentForm
} from '../components/aiAgents/AiAgentFormSections';
import { AI_AGENT_PROVIDERS, AI_AGENT_TYPES } from '../constants/aiAgents';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import AgenticAiUnavailable from '../components/aiAgents/AgenticAiUnavailable';
import { getAgenticAiServiceError } from '../utils/agenticAiErrors';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/ai-agents';
const DEFAULT_TEMPERATURE = 0.3;

const NewAiAgent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Platform-wide view picks the account; account / sub-account users are pinned to their own.
  const showAccountField = !user?.accountUID;

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(showAccountField ? (searchParams.get('accountId') || '') : '');
  const effectiveAccountId = showAccountField ? accountId : user?.accountUID;

  // Single-option catalogues are preselected; the model is always an explicit choice.
  const [form, setForm] = useState({
    name: '',
    role: '',
    type: AI_AGENT_TYPES.length === 1 ? AI_AGENT_TYPES[0].value : '',
    provider: AI_AGENT_PROVIDERS.length === 1 ? AI_AGENT_PROVIDERS[0].value : '',
    model: '',
    temperature: DEFAULT_TEMPERATURE,
    greetingMessage: '',
    prompt: '',
    knowledgeBaseIds: [],
    mcpServersText: '',
    mcpServersDirty: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [knowledgeBasesLoading, setKnowledgeBasesLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  // Set when the Agentic AI platform itself does not respond properly.
  const [serviceError, setServiceError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const listRoute = showAccountField && effectiveAccountId
    ? `${LIST_ROUTE}?accountId=${encodeURIComponent(effectiveAccountId)}`
    : LIST_ROUTE;

  useEffect(() => {
    if (!showAccountField) { return; }
    const fetchAccounts = async () => {
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        const list = data.data || data || [];
        setAccounts(list);
        // An unknown ?accountId= is treated as "no account selected".
        setAccountId((current) => (current && !list.some((a) => a.id === current) ? '' : current));
      } catch {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [showAccountField]);

  // Knowledge bases come from the account the agent is created under.
  useEffect(() => {
    setKnowledgeBases([]);
    setIntegrationError(null);
    setServiceError(null);
    setForm((f) => (f.knowledgeBaseIds.length ? { ...f, knowledgeBaseIds: [] } : f));
    if (!effectiveAccountId) { return undefined; }
    let cancelled = false;
    const fetchKnowledgeBases = async () => {
      try {
        setKnowledgeBasesLoading(true);
        const { data } = await knowledgeBaseService.getKnowledgeBases(
          showAccountField ? { accountId: effectiveAccountId } : {}
        );
        if (!cancelled) { setKnowledgeBases(Array.isArray(data?.data) ? data.data : []); }
      } catch (err) {
        if (cancelled) { return; }
        const message = getApiErrorMessage(err, 'Failed to load knowledge bases');
        if (getAgenticAiServiceError(err)) {
          setServiceError(getAgenticAiServiceError(err));
        } else if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
          setIntegrationError(message);
        } else {
          toastService.error(message);
        }
      } finally {
        if (!cancelled) { setKnowledgeBasesLoading(false); }
      }
    };
    fetchKnowledgeBases();
    return () => { cancelled = true; };
  }, [effectiveAccountId, showAccountField, reloadKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateAiAgentForm(form, { validateMcp: true });
    if (showAccountField && !accountId) { errors.accountId = `${ACCOUNT_LABEL.singular} is required`; }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) { return; }
    // An empty box creates the agent without MCP servers.
    const { servers: mcpServers } = parseMcpServers(form.mcpServersText);
    try {
      setSubmitting(true);
      const { data } = await aiAgentService.createAiAgent({
        ...(showAccountField && { accountId }),
        name: form.name.trim(),
        role: form.role.trim() || undefined,
        type: form.type,
        provider: form.provider,
        model: form.model,
        ...(form.temperature !== '' && { temperature: Number(form.temperature) }),
        greetingMessage: form.greetingMessage.trim() || undefined,
        prompt: form.prompt.trim() || undefined,
        knowledgeBaseIds: form.knowledgeBaseIds,
        ...(mcpServers && { mcpServers }),
      });
      toastService.success('AI agent created successfully');
      const newId = data?.data?.id;
      if (newId) {
        const query = showAccountField ? `?accountId=${encodeURIComponent(accountId)}` : '';
        navigate(`${LIST_ROUTE}/${encodeURIComponent(newId)}${query}`, {
          state: { agent: { id: newId, name: form.name.trim() } },
        });
      } else {
        navigate(listRoute);
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to create AI agent');
      if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      }
      toastService.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(listRoute)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to AI Agents
        </button>
      </div>

      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="space-y-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
            New Record
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
            Create AI Agent
          </h1>
          <p className="text-xs text-base-content/50">
            Configure a new AI agent and link it to the {ACCOUNT_LABEL.singularLower}'s knowledge bases.
          </p>
        </div>
      </div>

      {serviceError && (
        <AgenticAiUnavailable error={serviceError} onRetry={() => setReloadKey((k) => k + 1)} />
      )}

      {integrationError && (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      )}

      {/* Hidden while the Agentic AI service is down — the message screen above explains why. */}
      {!serviceError && (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account (platform-wide view only) */}
        {showAccountField && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">{ACCOUNT_LABEL.singular}</h2>
            </div>
            <div className="space-y-1.5 max-w-xl">
              <label htmlFor="accountId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                {ACCOUNT_LABEL.singular} *
              </label>
              <select
                id="accountId"
                className={inputClass('accountId')}
                value={accountId}
                onChange={(e) => {
                  setAccountId(e.target.value);
                  setFieldErrors((p) => ({ ...p, accountId: null }));
                }}
              >
                <option value="">— Select {ACCOUNT_LABEL.withArticle} —</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
              {fieldErrors.accountId && <p className="text-error text-xs font-medium">{fieldErrors.accountId}</p>}
              <p className="text-base-content/40 text-[10px] font-medium">
                The {ACCOUNT_LABEL.singularLower} must have AI Agent integration enabled. Its knowledge bases can be linked below.
              </p>
            </div>
          </div>
        )}

        <AiAgentFormSections
          form={form}
          setForm={setForm}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
          knowledgeBases={knowledgeBases}
          knowledgeBasesLoading={knowledgeBasesLoading}
          knowledgeBasesPlaceholder={effectiveAccountId ? '' : `Select ${ACCOUNT_LABEL.withArticle} first to choose its knowledge bases.`}
          showMcpServers
          mcpServersHint="Optional. A JSON array of MCP server objects, sent as-is. Leave it empty to create the agent without MCP servers."
        />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(listRoute)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || Boolean(integrationError) || Boolean(serviceError)}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Creating AI Agent...</span>
              </>
            ) : (
              <>
                <PlusCircle size={16} />
                <span>Create AI Agent</span>
              </>
            )}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};

export default NewAiAgent;
